import * as THREE from 'three';

export type WeatherPreset = 'clear' | 'fair' | 'overcast' | 'rain' | 'storm' | 'snow' | 'fog' | 'winter';

export interface WeatherState {
  cloudCoverage: number;
  cloudBase: number;
  cloudThickness: number;
  cloudDensity: number;
  cloudType: number;
  cloudDarkness: number;
  cloudErosion: number;
  cloudAnvil: number;
  haze: number; // Mie scale
  fogDensity: number; // 1/m at ground
  fogScale: number; // m
  valleyFog: number; // 1/m
  valleyTop: number; // m ASL
  rain: number;
  snow: number;
  wetness: number; // target
  snowCover: number; // target
  windSpeed: number; // m/s
  windDir: number; // radians, direction the wind blows toward (0 = +x)
  lightning: number;
}

export const PRESETS: Record<WeatherPreset, WeatherState> = {
  clear: {
    cloudCoverage: 0.0, cloudBase: 4500, cloudThickness: 1800, cloudDensity: 1, cloudType: 1, cloudDarkness: 0, cloudErosion: 0.35, cloudAnvil: 0,
    haze: 0.35, fogDensity: 0, fogScale: 300, valleyFog: 0, valleyTop: 2330, rain: 0, snow: 0, wetness: 0, snowCover: 0, windSpeed: 3, windDir: 0.4, lightning: 0,
  },
  fair: {
    cloudCoverage: 0.5, cloudBase: 4300, cloudThickness: 2200, cloudDensity: 1, cloudType: 1, cloudDarkness: 0.05, cloudErosion: 0.38, cloudAnvil: 0,
    haze: 0.5, fogDensity: 0, fogScale: 300, valleyFog: 0, valleyTop: 2330, rain: 0, snow: 0, wetness: 0, snowCover: 0, windSpeed: 6, windDir: 0.5, lightning: 0,
  },
  overcast: {
    cloudCoverage: 0.92, cloudBase: 3500, cloudThickness: 1500, cloudDensity: 1.2, cloudType: 0.35, cloudDarkness: 0.35, cloudErosion: 0.25, cloudAnvil: 0,
    haze: 1.3, fogDensity: 0.00004, fogScale: 500, valleyFog: 0, valleyTop: 2330, rain: 0, snow: 0, wetness: 0, snowCover: 0, windSpeed: 7, windDir: 0.6, lightning: 0,
  },
  rain: {
    cloudCoverage: 0.97, cloudBase: 3250, cloudThickness: 2600, cloudDensity: 1.5, cloudType: 0.6, cloudDarkness: 0.62, cloudErosion: 0.2, cloudAnvil: 0,
    haze: 2.6, fogDensity: 0.00022, fogScale: 600, valleyFog: 0.0006, valleyTop: 2360, rain: 0.8, snow: 0, wetness: 1, snowCover: 0, windSpeed: 8, windDir: 0.7, lightning: 0,
  },
  storm: {
    cloudCoverage: 0.88, cloudBase: 3500, cloudThickness: 8500, cloudDensity: 1.6, cloudType: 2, cloudDarkness: 0.82, cloudErosion: 0.3, cloudAnvil: 0.8,
    haze: 2.2, fogDensity: 0.00018, fogScale: 600, valleyFog: 0, valleyTop: 2330, rain: 1, snow: 0, wetness: 1, snowCover: 0, windSpeed: 13, windDir: 0.9, lightning: 1,
  },
  snow: {
    cloudCoverage: 0.98, cloudBase: 3050, cloudThickness: 1900, cloudDensity: 1.3, cloudType: 0.3, cloudDarkness: 0.4, cloudErosion: 0.2, cloudAnvil: 0,
    haze: 3.2, fogDensity: 0.00045, fogScale: 700, valleyFog: 0.0008, valleyTop: 2380, rain: 0, snow: 1, wetness: 0.2, snowCover: 0.85, windSpeed: 4, windDir: 0.3, lightning: 0,
  },
  fog: {
    cloudCoverage: 0.18, cloudBase: 5200, cloudThickness: 900, cloudDensity: 0.6, cloudType: 0.2, cloudDarkness: 0, cloudErosion: 0.4, cloudAnvil: 0,
    haze: 0.9, fogDensity: 0.00003, fogScale: 250, valleyFog: 0.018, valleyTop: 2365, rain: 0, snow: 0, wetness: 0.25, snowCover: 0, windSpeed: 1.5, windDir: 0.2, lightning: 0,
  },
  winter: {
    cloudCoverage: 0.25, cloudBase: 4600, cloudThickness: 1500, cloudDensity: 0.9, cloudType: 0.9, cloudDarkness: 0, cloudErosion: 0.4, cloudAnvil: 0,
    haze: 0.3, fogDensity: 0, fogScale: 300, valleyFog: 0, valleyTop: 2330, rain: 0, snow: 0, wetness: 0, snowCover: 0.8, windSpeed: 3, windDir: 0.2, lightning: 0,
  },
};

export const PRESET_LABELS: Record<WeatherPreset, string> = {
  clear: 'Clear', fair: 'Fair clouds', overcast: 'Overcast', rain: 'Rain', storm: 'Thunderstorm', snow: 'Snowfall', fog: 'Valley fog', winter: 'Snow cover',
};

/**
 * Weather simulation: smooth transitions between presets, plus slow ground accumulation (wetness / snow).
 */
export class Weather {
  preset: WeatherPreset = 'clear';
  readonly current: WeatherState = { ...PRESETS.clear };
  private target: WeatherState = { ...PRESETS.clear };
  /** accumulated surface state */
  wetness = 0;
  snowCover = 0;
  lightningFlash = 0;
  readonly lightningPos = new THREE.Vector3();
  onThunder: ((delay: number, intensity: number) => void) | null = null;
  onLightning: ((pos: THREE.Vector3) => void) | null = null;
  private nextStrike = 4;
  private flashT = 0;
  transitionSeconds = 6;
  /** override controls (null = follow preset) */
  overrides: Partial<Pick<WeatherState, 'cloudCoverage' | 'valleyFog' | 'rain' | 'snow' | 'windSpeed'>> & { snowCover?: number } = {};
  readonly wind = new THREE.Vector2();

  set(p: WeatherPreset, instant = false) {
    this.preset = p;
    this.target = { ...PRESETS[p] };
    this.overrides = {};
    if (instant) {
      Object.assign(this.current, this.target);
      this.wetness = this.target.wetness;
      this.snowCover = this.target.snowCover;
    }
  }

  update(dt: number, camPos: THREE.Vector3) {
    const k = 1 - Math.exp(-dt * (3 / this.transitionSeconds));
    const t = { ...this.target, ...this.overrides } as WeatherState;
    for (const key of Object.keys(this.current) as (keyof WeatherState)[]) {
      this.current[key] += (t[key] - this.current[key]) * k;
    }
    // surfaces: wetness follows rain quickly, dries slowly; snow builds while snowing
    const wetT = Math.max(t.wetness, this.current.rain);
    this.wetness += (wetT - this.wetness) * (1 - Math.exp(-dt * (wetT > this.wetness ? 0.25 : 0.05)));
    const snowT = this.overrides.snowCover ?? t.snowCover;
    this.snowCover += (snowT - this.snowCover) * (1 - Math.exp(-dt * (snowT > this.snowCover ? 0.18 : 0.08)));
    this.wind.set(Math.cos(this.current.windDir), Math.sin(this.current.windDir)).multiplyScalar(this.current.windSpeed);

    // lightning
    this.lightningFlash = 0;
    if (this.current.lightning > 0.3) {
      this.nextStrike -= dt * this.current.lightning;
      if (this.nextStrike <= 0) {
        this.nextStrike = 3 + Math.random() * 11;
        const ang = Math.random() * Math.PI * 2, dist = 1500 + Math.random() * 9000;
        this.lightningPos.set(camPos.x + Math.cos(ang) * dist, this.current.cloudBase + 600, camPos.z + Math.sin(ang) * dist);
        this.flashT = 0.0001;
        this.onLightning?.(this.lightningPos);
        this.onThunder?.(dist / 343, Math.min(1, 3000 / dist));
      }
    }
    if (this.flashT > 0) {
      this.flashT += dt;
      const f = this.flashT;
      // a couple of return strokes
      this.lightningFlash = Math.max(0, Math.exp(-f * 18) + 0.6 * Math.exp(-Math.abs(f - 0.12) * 40) + 0.35 * Math.exp(-Math.abs(f - 0.26) * 30));
      if (f > 0.7) this.flashT = 0;
    }
  }
}
